import { useMediaQuery, useTheme } from "@mui/material";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";

import { useThemeMode } from "../../hooks/useThemeMode";
import { LeftNavDrawerV2, type NavSection } from "./LeftNavDrawerV2";
import { MainV2 } from "./MainV2";
import { TopNavBarV2 } from "./TopNavBarV2";

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function isDesktopDrawerToggleShortcut(
  event: Pick<KeyboardEvent, "ctrlKey" | "key" | "metaKey" | "shiftKey">
): boolean {
  const isB = event.key.toLowerCase() === "b";
  const isMod = event.metaKey || event.ctrlKey;

  return isB && isMod && !event.shiftKey;
}

export function LayoutV2(props: PropsWithChildren<{ navSections: NavSection[] }>) {
  const { toggleThemeMode } = useThemeMode();
  const theme = useTheme();
  const expandedWidth = theme.spacing(25);
  const collapsedWidth = theme.spacing(9);
  /** True when the left nav switches to the mobile-style overlay drawer behavior. */
  const isMobileDrawerMode = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [collapsed, setCollapsed] = useState(false);
  const [temporaryOpen, setTemporaryOpen] = useState(false);

  const handleDesktopDrawerToggle = useCallback(() => {
    setCollapsed((current) => !current);
  }, []);

  const handleTemporaryDrawerToggle = useCallback(() => {
    setTemporaryOpen((current) => !current);
  }, []);

  const handleTemporaryDrawerClose = useCallback(() => {
    setTemporaryOpen(false);
  }, []);

  useEffect(() => {
    if (!isMobileDrawerMode) {
      setTemporaryOpen(false);
    }
  }, [isMobileDrawerMode]);

  useEffect(() => {
    if (isMobileDrawerMode) {
      return;
    }

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (isEditableElement(event.target)) return;

      if (isDesktopDrawerToggleShortcut(event)) {
        event.preventDefault();
        handleDesktopDrawerToggle();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleDesktopDrawerToggle, isMobileDrawerMode]);

  return (
    <>
      <LeftNavDrawerV2
        navSections={props.navSections}
        collapsed={collapsed}
        expandedWidth={expandedWidth}
        collapsedWidth={collapsedWidth}
        open={temporaryOpen}
        isMobileDrawerMode={isMobileDrawerMode}
        isMobile={isMobile}
        onClose={handleTemporaryDrawerClose}
        onNavToggle={handleDesktopDrawerToggle}
      />
      <MainV2 collapsed={collapsed} expandedWidth={expandedWidth} collapsedWidth={collapsedWidth}>
        <TopNavBarV2
          handleThemeToggle={toggleThemeMode}
          onMenuClick={handleTemporaryDrawerToggle}
        />
        {props.children}
      </MainV2>
    </>
  );
}
